import type { Permission } from "src/api/client-api/permissions/useGetAllPermissions";
import { MODIFY, PRODUCT_ACTIVITY_DATA, VIEW } from "src/context/FoodpilotContext";

type PermissionMatcher = Pick<Permission, "object" | "slug">;

type PermissionDependencyRule = {
    permission: PermissionMatcher;
    requires: PermissionMatcher[];
};

const PERMISSION_DEPENDENCY_RULES: PermissionDependencyRule[] = [
    {
        permission: { object: PRODUCT_ACTIVITY_DATA, slug: MODIFY },
        requires: [{ object: PRODUCT_ACTIVITY_DATA, slug: VIEW }],
    },
];

const matchesPermission = (permission: Permission, matcher: PermissionMatcher) => {
    return permission.object === matcher.object && permission.slug === matcher.slug;
};

const findPermissionId = (permissions: Permission[], matcher: PermissionMatcher) => {
    return permissions.find((permission) => matchesPermission(permission, matcher))?.["@id"];
};

export const applyPermissionDependencies = (selectedPermissions: Set<string>, allPermissions: Permission[]) => {
    const next = new Set(selectedPermissions);

    PERMISSION_DEPENDENCY_RULES.forEach((rule) => {
        const permissionId = findPermissionId(allPermissions, rule.permission);

        if (!permissionId || !next.has(permissionId)) {
            return;
        }

        rule.requires.forEach((requirement) => {
            const requiredPermissionId = findPermissionId(allPermissions, requirement);

            if (requiredPermissionId) {
                next.add(requiredPermissionId);
            }
        });
    });

    return next;
};

export const togglePermissionSelection = (
    selectedPermissions: Set<string>,
    permissionId: string,
    allPermissions: Permission[],
) => {
    const next = new Set(selectedPermissions);
    const wasSelected = next.has(permissionId);

    if (wasSelected) {
        next.delete(permissionId);
    } else {
        next.add(permissionId);
    }

    PERMISSION_DEPENDENCY_RULES.forEach((rule) => {
        const permissionDependsOnToggledPermission = rule.requires.some((requirement) => {
            return findPermissionId(allPermissions, requirement) === permissionId;
        });

        if (!wasSelected || !permissionDependsOnToggledPermission) {
            return;
        }

        const dependentPermissionId = findPermissionId(allPermissions, rule.permission);

        if (dependentPermissionId) {
            next.delete(dependentPermissionId);
        }
    });

    return applyPermissionDependencies(next, allPermissions);
};
